import { createFileRoute, Link, notFound, redirect } from "@tanstack/react-router";
import { lazy, Suspense } from "react";
import { NotFoundView, DefaultRouteError } from "@/components/RouteFallbacks";
import { relatedGuides } from "@/lib/related-guides";
import type { Guide, GuideArticle } from "@/lib/guides-data";
import { ShareButton } from "@/components/ShareButton";
import { clipTitle, pageTitle } from "@/lib/seo-title";
import { threeDayBucket } from "@/lib/rotation";
import { officialLinkPool, OUTBOUND_REL } from "@/lib/official-links";
import { howToSchema } from "@/lib/schema";
import { MoneyPageLinks } from "@/components/MoneyPageLinks";
import ss01KeyVisual from "@/assets/ss01/slide.webp.asset.json";
import { ContextVisualStrip, gameplayImagePath, guideVisualVariant } from "@/components/ContextVisualStrip";
import { Ss01CardPair } from "@/components/Ss01CardPair";
import { SS01_CARDS } from "@/lib/ss01-cards";

import { isValidInternalPath } from "@/lib/data-integrity";
import type { ReactNode } from "react";

const GuideCardGallery = lazy(() =>
  import("@/components/GuideCardGallery").then((m) => ({ default: m.GuideCardGallery })),
);

const Ss01Showcase = lazy(() => import("@/components/Ss01Showcase").then((m) => ({ default: m.Ss01Showcase })));

// Auto-link internal paths ("/guides/foo", "/pals", "/cards", etc.) inside article body copy.
// Only paths that resolve to a real route become links — this stops fractions ("8/10"),
// rates ("50/hour") and file paths ("/Binaries/Win64") from turning into dead 404 links.
function linkify(text: string): ReactNode[] {
  const re = /(\/[a-z0-9][a-z0-9\-/]*)/gi;
  const out: ReactNode[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  let key = 0;
  while ((m = re.exec(text)) !== null) {
    const path = m[1].replace(/[.,;:!?)]+$/, "");
    if (!isValidInternalPath(path)) continue;
    if (last < m.index) out.push(text.slice(last, m.index));
    out.push(
      <Link key={key++} to={path as never} className="text-primary hover:underline">
        {path}
      </Link>
    );
    last = m.index + path.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out.length ? out : [text];
}


export const Route = createFileRoute("/guides/$slug/$article")({
  // Dynamic import keeps the full guide corpus out of the sitewide entry chunk;
  // it is only fetched when a guide article is actually opened.
  loader: async ({ params }): Promise<{ guide: Guide; article: GuideArticle; articleIndex: number }> => {
    const { GUIDE_BY_SLUG } = await import("@/lib/guides-data");
    const guide = GUIDE_BY_SLUG[params.slug];
    if (!guide) {
      // Retired fan-variant guide slugs: 301 to the base guide's article when it
      // exists, otherwise to the base guide hub.
      const { resolveRetiredSlug, retiredSectionIndex } = await import("@/lib/seo-redirects");
      const base = resolveRetiredSlug(params.slug, (s) => !!GUIDE_BY_SLUG[s]);
      if (base) {
        const hasArticle = GUIDE_BY_SLUG[base].articles.some((a) => a.slug === params.article);
        throw redirect({
          href: hasArticle ? `/guides/${base}/${params.article}` : `/guides/${base}`,
          statusCode: 301,
        });
      }
      throw redirect({ href: retiredSectionIndex("guides"), statusCode: 301 });
    }
    // Unknown article under a real guide: send readers to that guide's hub.
    const articleIndex = guide.articles.findIndex((a) => a.slug === params.article);
    if (articleIndex === -1) throw redirect({ href: `/guides/${params.slug}`, statusCode: 301 });
    return { guide, article: guide.articles[articleIndex], articleIndex };
  },

  head: ({ loaderData }) => {
    if (!loaderData) return { meta: [{ title: "Article not found — Pal Stars" }, { name: "robots", content: "noindex" }] };
    const { guide, article } = loaderData;
    const clip = clipTitle;
    const title = pageTitle(article.title);
    const desc = clip(article.intro, 155);
    const url = `https://palstars.com/guides/${guide.slug}/${article.slug}`;
    const visualVariant = guideVisualVariant(`${guide.title} ${guide.desc} ${article.title} ${article.intro}`);
    const image = article.showcase === "ss01"
      ? `https://palstars.com${ss01KeyVisual.url}`
      : `https://palstars.com${gameplayImagePath(visualVariant)}`;
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: desc },
        { name: "robots", content: "index, follow, max-snippet:-1, max-image-preview:large" },
        ...(image ? [{ property: "og:image", content: image }, { name: "twitter:image", content: image }] : []),
      ],

      links: [{ rel: "canonical", href: url }],
      scripts: (() => {
        const faqEntities = (() => {
          const seen = new Set<string>();
          return article.sections
            .map((s) => {
              const q = s.heading.trim();
              const name = q.endsWith("?") ? q : `${q}?`;
              return { name, text: (s.text ?? "").trim() };
            })
            .filter((q) => {
              if (!q.name || !q.text) return false;
              if (seen.has(q.name)) return false;
              seen.add(q.name);
              return true;
            })
            .map((q) => ({
              "@type": "Question",
              name: q.name,
              acceptedAnswer: { "@type": "Answer", text: q.text },
            }));
        })();
        const scripts: { type: string; children: string }[] = [
          {
            type: "application/ld+json",
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Article",
              headline: article.title,
              description: article.intro,
              ...(image ? { image } : {}),
              author: { "@type": "Organization", name: "Pal Stars", url: "https://palstars.com/", logo: "https://palstars.com/eidrolon-avatar.png" },
              publisher: { "@type": "Organization", name: "Pal Stars", url: "https://palstars.com/", logo: "https://palstars.com/eidrolon-avatar.png" },
              speakable: { "@type": "SpeakableSpecification", cssSelector: ["h1", "h2", ".aeo-answer"] },
            }),
          },
        ];
        // Articles whose visible sections are numbered steps ("1. Do this")
        // qualify as HowTo content, which is a richer entity than Article alone.
        const stepSections = article.sections.filter((s) => /^\d+\.\s/.test(s.heading.trim()));
        if (stepSections.length >= 3 && stepSections.length === article.sections.length) {
          scripts.push(
            howToSchema({
              name: article.title,
              description: article.intro,
              path: `/guides/${guide.slug}/${article.slug}`,
              steps: stepSections.map((s) => ({
                name: s.heading.replace(/^\d+\.\s*/, ""),
                text: s.text,
              })),
            }),
          );
        } else if (faqEntities.length > 0) {
          scripts.push({
            type: "application/ld+json",
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              mainEntity: faqEntities,
            }),
          });
        }
        return scripts;
      })().concat([
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: { "@id": "https://palstars.com/", name: "Home" } },
              { "@type": "ListItem", position: 2, name: "Guides", item: { "@id": "https://palstars.com/guides", name: "Guides" } },
              { "@type": "ListItem", position: 3, name: guide.title, item: { "@id": `https://palstars.com/guides/${encodeURIComponent(guide.slug)}`, name: guide.title } },
              { "@type": "ListItem", position: 4, name: article.title, item: { "@id": `https://palstars.com/guides/${encodeURIComponent(guide.slug)}/${encodeURIComponent(article.slug)}`, name: article.title } },
            ],
          }),
        },
      ]),
    };
  },
  component: ArticleDetail,
  errorComponent: DefaultRouteError,
  notFoundComponent: () => (
    <NotFoundView
      title="That article doesn't exist"
      message="This guide article couldn't be found. Open the guide index to see all five articles in each Palworld guide."
    />
  ),
});

function ArticleDetail() {
  const { guide, article, articleIndex } = Route.useLoaderData() as { guide: Guide; article: GuideArticle; articleIndex: number };
  const prev = guide.articles[(articleIndex - 1 + guide.articles.length) % guide.articles.length];
  const next = guide.articles[(articleIndex + 1) % guide.articles.length];
  // Only official Pocketpair / Bushiroad sources are linked out (see
  // src/lib/official-links.ts) — third-party news links rotted into dead pages.
  const pool = officialLinkPool(article.externalLinks);
  const rotated = pool[(threeDayBucket() + articleIndex) % pool.length];

  const ss01CardForSection = (heading: string) =>
    SS01_CARDS.find((card) => heading.includes(card.code) || heading.includes(card.nameEn));


  return (
    <article className="mx-auto max-w-3xl px-4 sm:px-6 py-10">
      <div className="mb-4 flex flex-wrap gap-2">
        <Link to="/guides/$slug" params={{ slug: guide.slug }} className="inline-flex items-center gap-1 rounded-full border bg-card/60 px-3 py-1.5 text-xs hover:bg-card">← Back to {guide.title}</Link>
        <Link to="/guides" className="inline-flex items-center gap-1 rounded-full border bg-card/60 px-3 py-1.5 text-xs hover:bg-card">All Guides</Link>
      </div>
      <nav className="text-xs text-muted-foreground mb-6">
        <Link to="/" className="hover:text-foreground">Home</Link> /{" "}
        <Link to="/guides" className="hover:text-foreground">Guides</Link> /{" "}
        <Link to="/guides/$slug" params={{ slug: guide.slug }} className="hover:text-foreground">{guide.title}</Link> /{" "}
        <span className="text-foreground">{article.title}</span>
      </nav>

      <span className="text-[10px] uppercase tracking-widest text-primary">{guide.tag} · Article {articleIndex + 1}</span>
      <h1 className="mt-2 text-3xl sm:text-4xl font-bold">{article.title}</h1>
      <p className="aeo-answer mt-3 text-muted-foreground leading-relaxed">{article.intro}</p>
      <div className="mt-3 flex items-center gap-3 flex-wrap">
        <p className="text-xs text-muted-foreground">Updated for the current Palworld patch</p>
        <ShareButton title={article.title} text={article.intro} path={`/guides/${guide.slug}/${article.slug}`} />
      </div>
      {article.showcase !== "ss01" && (
        <ContextVisualStrip variant={guideVisualVariant(`${guide.title} ${guide.desc} ${article.title} ${article.intro}`)} />
      )}
      <div className="mt-6 rounded-lg border bg-background/40 p-4">
        <div className="text-[10px] uppercase tracking-widest text-primary mb-1">Key takeaways</div>
        <ul className="grid gap-1 text-sm">
          {article.sections.slice(0, 4).map((s, i) => (
            <li key={i} className="flex gap-2"><span className="text-primary">✓</span><span>{s.heading}</span></li>
          ))}
        </ul>
      </div>

      <div className="mt-8 space-y-5 glass rounded-2xl p-6 sm:p-8">
        {article.sections.map((s, i) => (
          <div key={i}>
            <h2 className="text-base font-semibold">{s.heading}</h2>
            <p className="aeo-answer mt-1 text-sm text-muted-foreground leading-relaxed">{linkify(s.text)}</p>
            {article.slug === "card-list" && ss01CardForSection(s.heading) ? (
              <Ss01CardPair card={ss01CardForSection(s.heading) as (typeof SS01_CARDS)[number]} />
            ) : null}
          </div>
        ))}
        {article.showcase === "ss01" && article.slug !== "card-list" && (
          <Suspense fallback={<div className="mt-6 h-64 rounded-xl border bg-card/40" aria-hidden="true" />}>
            <Ss01Showcase />
          </Suspense>
        )}
        {article.cards && article.cards.length > 0 && (
          <Suspense fallback={<div className="mt-6 aspect-[420/240] rounded-xl border bg-card/40" aria-hidden="true" />}>
            <GuideCardGallery
              codes={article.cards}
              caption="Official Palworld OFFICIAL CARD GAME scans — click any card to expand. Card images © Pocketpair / Bushiroad, shown for identification."
            />
          </Suspense>
        )}
      </div>

      <div className="mt-6 rounded-lg border border-primary/30 bg-primary/5 p-4 text-sm">
        <div className="text-[10px] uppercase tracking-widest text-primary mb-1">Official sources</div>
        <a href={rotated.url} target="_blank" rel={OUTBOUND_REL} className="font-semibold text-primary hover:underline">
          {rotated.label} ↗
        </a>
        <p className="mt-1 text-xs text-muted-foreground">{rotated.note}</p>
        <div className="mt-2 text-[11px] text-muted-foreground">
          More:{" "}
          {pool.filter((l) => l.url !== rotated.url).map((l, i, arr) => (
            <span key={l.url}>
              <a href={l.url} target="_blank" rel={OUTBOUND_REL} className="underline hover:text-primary">{l.label}</a>

              {i < arr.length - 1 ? " · " : ""}
            </span>
          ))}
        </div>
      </div>

      <div className="mt-8 flex justify-between text-sm">
        <Link to="/guides/$slug/$article" params={{ slug: guide.slug, article: prev.slug }} className="text-primary hover:underline">← {prev.title}</Link>
        <Link to="/guides/$slug/$article" params={{ slug: guide.slug, article: next.slug }} className="text-primary hover:underline">{next.title} →</Link>
      </div>

      <div className="mt-10 rounded-2xl border bg-card/40 p-6">
        <h2 className="text-lg font-semibold">More in {guide.title}</h2>
        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          {guide.articles.filter((a) => a.slug !== article.slug).map((a) => (
            <Link key={a.slug} to="/guides/$slug/$article" params={{ slug: guide.slug, article: a.slug }} className="rounded-lg border bg-background/40 p-3 text-sm hover:bg-card">
              <span className="font-medium">{a.title}</span>
              <span className="mt-1 block text-xs text-primary">Read article →</span>
            </Link>
          ))}
        </div>
        {/* Kept deliberately short: guide-to-guide links used to outnumber links
            to the hubs, which trapped internal PageRank inside the guide cluster. */}
        <div className="mt-4 flex flex-wrap gap-2 text-xs">
          {relatedGuides(guide, 3).map((g) => (
            <Link key={g.slug} to="/guides/$slug" params={{ slug: g.slug }} className="rounded-full border px-3 py-1.5 hover:bg-card">{g.title}</Link>
          ))}
        </div>
      </div>

      <MoneyPageLinks topic={`${guide.title} ${guide.tag} ${guide.keywords ?? ""}`} />
    </article>
  );
}
